import classNames from 'classnames';
import dayjs from 'dayjs';
import { useContext, useState, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';

import { Image } from '@/shared/ui';

import { normalizeDate } from '@/modules/erz/helpers/normalizeDate';

import AuthContext from '../../contexts/Auth/AuthContext';
import { getDateWithoutTime } from '../../helpers/dates';
import { FormInputWrapper } from '../old';

const todayDay = getDateWithoutTime(new Date());
const hundredYearsAgo = new Date();
const hundredYearsLater = new Date();
hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
hundredYearsLater.setFullYear(hundredYearsLater.getFullYear() + 100);

interface Props {
  label?: string;
  placeholder?: string;
  format?: string;
  mask?: string;
  value: Nullable<Date>;
  name: string;
  minDate?: Date;
  maxDate?: Date;
  maxDateToday?: boolean;
  minDateToday?: boolean;
  minDateBirth?: boolean;
  required?: boolean;
  disabled?: boolean;
  onChange: (date: Date) => void;
}

const InputDate: React.FC<Props> = ({
  label,
  value,
  placeholder = 'дд.мм.рррр',
  format = 'dd.MM.yyyy',
  mask = '99.99.9999',
  name,
  minDate,
  maxDate,
  minDateToday = false,
  maxDateToday = false,
  minDateBirth = false,
  required = false,
  disabled = false,
  onChange,
}) => {
  const { userData } = useContext(AuthContext);
  const [inputError, setInputError] = useState<string>('');

  const copyValue = new Date(value || 0);

  const internalValue = useMemo<typeof value>(() => {
    if (!copyValue) return copyValue;

    const utcOffsetInMinutes = dayjs().utcOffset();
    const utcOffsetInHours = utcOffsetInMinutes / 60;

    if (utcOffsetInHours < 0 && copyValue.getUTCHours() !== utcOffsetInHours * -1) {
      copyValue.setUTCHours(utcOffsetInHours * -1);
    }
    if (utcOffsetInHours >= 0 && copyValue.getUTCHours() !== 0) {
      copyValue.setUTCHours(0);
    }

    return value;
  }, [copyValue]);

  useEffect(() => {
    if (value) validateField(String(value));
  }, []);

  const validateField = (inputValue = ''): void => {
    if (required && !inputValue) {
      setInputError('Необхідно заповнити поле');
      return;
    }
    setInputError('');
  };

  const handleChange = (date: Date): void => {
    onChange(normalizeDate(date));
    validateField(String(internalValue));
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>): void => {
    validateField(event.target.value);
  };

  const getCalculatedMinDate = (): Date => {
    if (minDate) {
      return minDate;
    } else if (minDateBirth && userData && userData.date_of_birth) {
      return new Date(userData.date_of_birth);
    } else if (minDateToday) {
      return todayDay;
    } else {
      return hundredYearsAgo;
    }
  };

  const getCalculatedMaxDate = (): Date => {
    if (maxDate) {
      return maxDate;
    } else if (maxDateToday) {
      return todayDay;
    } else {
      return hundredYearsLater;
    }
  };
  const calculatedMaxDate = getCalculatedMaxDate();
  const calculatedMinDate = getCalculatedMinDate();

  return (
    <FormInputWrapper
      label={label}
      required={required}
      marginBottomNone
      disabled={disabled}
      className={classNames({
        'disabled-div': disabled,
        'error-div': !!inputError,
      })}
    >
      <div className="form-input__date-box">
        <DatePicker
          locale="uk"
          name={name}
          id={name}
          placeholderText={placeholder}
          autoComplete="off"
          disabled={disabled}
          selected={internalValue}
          className="form-input__input  form-control"
          dateFormat={format}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          minDate={calculatedMinDate}
          maxDate={calculatedMaxDate}
          onBlur={handleBlur}
          required={required}
          customInput={
            <InputMask mask={mask} name={name} value={internalValue ? internalValue.toLocaleDateString('uk-UA') : ''} />
          }
          onChange={handleChange}
          onKeyDown={(e): void => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />
        <label htmlFor={name}>
          <Image
            className="has-datetimepicker form-input__date-icon js-date-picker"
            src="/images/calendar.svg"
            alt="icon"
          />
        </label>
        {inputError && (
          <div className="error-msg" key={inputError}>
            {inputError}
          </div>
        )}
      </div>
    </FormInputWrapper>
  );
};

export default InputDate;
